/**
 * Engage globalAdmin shadow-profile list discoverability (filters, strip, summaries).
 */

import { useCallback, useMemo, useState } from 'react';
import BeerProfileShadowDiscoverabilityBar from '../components/beer/BeerProfileShadowDiscoverabilityBar';
import {
  beerMatchesShadowProfileFilter,
  countBeerProfileShadowSummaries,
  SHADOW_PROFILE_FILTER_ALL,
} from '../utils/beer/beerProfileComparison';
import useBreweryBeerProfileShadowSummaries from './useBreweryBeerProfileShadowSummaries';

/**
 * @param {Array<{ id: string }>} locationFilteredBeers
 * @param {boolean} enabled — true when globalAdmin shadow UI is active
 * @returns {{
 *   filteredBeers: Array<{ id: string }>,
 *   shadowDiscoverabilityControl: import('react').ReactNode | null,
 *   shadowSummariesByBeerId: Map<string, ReturnType<import('../utils/beer/beerProfileComparison').deriveBeerProfileShadowSummary>> | null,
 * }}
 */
export default function useBreweryBeerShadowDiscoverability(
  locationFilteredBeers,
  enabled,
) {
  const [shadowProfileFilter, setShadowProfileFilter] = useState(
    SHADOW_PROFILE_FILTER_ALL,
  );

  const locationFilteredBeerIds = useMemo(
    () => (Array.isArray(locationFilteredBeers)
      ? locationFilteredBeers.map((beerRow) => beerRow.id)
      : []),
    [locationFilteredBeers],
  );

  const {
    summariesByBeerId: shadowSummariesByBeerId,
    loading: shadowSummariesLoading,
    error: shadowSummariesError,
  } = useBreweryBeerProfileShadowSummaries(
    enabled ? locationFilteredBeerIds : null,
  );

  const shadowStripCounts = useMemo(
    () => countBeerProfileShadowSummaries(
      locationFilteredBeerIds,
      shadowSummariesByBeerId,
    ),
    [locationFilteredBeerIds, shadowSummariesByBeerId],
  );

  const filteredBeers = useMemo(() => {
    const beers = Array.isArray(locationFilteredBeers) ? locationFilteredBeers : [];
    if (!enabled || shadowProfileFilter === SHADOW_PROFILE_FILTER_ALL) {
      return beers;
    }
    return beers.filter((beerRow) =>
      beerMatchesShadowProfileFilter(
        shadowSummariesByBeerId.get(beerRow.id),
        shadowProfileFilter,
      ),
    );
  }, [
    locationFilteredBeers,
    enabled,
    shadowProfileFilter,
    shadowSummariesByBeerId,
  ]);

  const handleShadowProfileFilterChange = useCallback((_event, nextFilter) => {
    if (nextFilter == null) {
      return;
    }
    setShadowProfileFilter(nextFilter);
  }, []);

  const shadowDiscoverabilityControl = enabled ? (
    <BeerProfileShadowDiscoverabilityBar
      filter={shadowProfileFilter}
      onFilterChange={handleShadowProfileFilterChange}
      counts={shadowStripCounts}
      loading={shadowSummariesLoading}
      error={shadowSummariesError}
    />
  ) : null;

  return {
    filteredBeers,
    shadowDiscoverabilityControl,
    shadowSummariesByBeerId: enabled ? shadowSummariesByBeerId : null,
  };
}
